import { useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
    MDBModal,
    MDBModalDialog,
    MDBModalContent,
    MDBModalHeader,
    MDBModalTitle,
    MDBModalBody,
    MDBModalFooter,
    MDBBtn,
    MDBCheckbox,
    MDBCard,
    MDBCardBody,
    MDBCardTitle,
    MDBSpinner,
    MDBTypography
} from "mdb-react-ui-kit";
import { generateClient } from 'aws-amplify/api';
import { UserContext } from "../../App";
import { captureError } from "../../utils/sentryHelpers";
import LangSelect from "../../components-user/LangSelect";

const client = generateClient();

const ONBOARDING_V2_CONDITION_ID = "onboarding-v2-lounges-2026";

// Content in both languages
const content = {
    de: {
        title: "Account aktualisieren und LOUNGES vorbereiten",
        stepTitle: "2. Onboarding",
        // Section 1
        accountUpdate: {
            title: "Account aktualisieren",
            items: [
                { bold: "Mitarbeiterliste prüfen", text: "Ist diese noch aktuell?", subtext: "Neue Mitarbeiter eintragen und Personen, die nicht mehr im Unternehmen sind löschen" },
                { bold: "Produkte checken", text: "Sind die eingetragenen Produkte noch die richtigen?", subtext: "Neue Produkte eintragen und nicht mehr aktuelle Produkte bitte löschen" },
                { bold: "News eintragen", text: "Sind die wichtigsten Unternehmens-News im System eingetragen?", subtext: "News ergänzen und prüfen, ob eingetragene News noch eingetragen bleiben sollen" }
            ],
            checkbox: "Ich habe diese Punkte gelesen und verstanden und aktualisiere den Account"
        },
        // Section 2
        imageRights: {
            title: "Nutzungsrechte für Bilder, die im System hochgeladen sind",
            intro: "Hier benötigen wir eine Bestätigung.",
            checkbox: "Ich bestätige hiermit, dass das Unternehmen über alle erforderlichen Rechte an dem hochgeladenen Bild verfügt und dass durch die Veröffentlichung keine Rechte Dritter verletzt werden. Ich stelle die Inspire GmbH von allen Ansprüchen Dritter frei."
        },
        // Section 3
        boothConstruction: {
            title: "Kein eigener Standbau",
            intro: "Auf den LOUNGES ist kein eigener Standbau gestattet.",
            items: [
                "Sondergenehmigungen müssen angefragt und von der Inspire GmbH freigeben werden",
                "Auf dem Stand dürfen nur ein Roll-Up oder eine Pixlip-Wand mit der maximalen Breite von 1,00 m und der maximalen Höhe 2,50 m aufgestellt werden",
                "Sollten diese Bedingungen nicht erfüllt sein, wird der Ausstellungsstand nicht freigegeben",
                "Die Inspire GmbH ist berechtigt nicht genehmigte Standbauten abbauen zu lassen"
            ],
            checkbox: "Ich habe diese Punkte gelesen und verstanden und akzeptiere die Bedingungen"
        },
        // Section 4
        visitorRegistration: {
            title: "Besuchereinladungen / Registrierung von Standpersonal und Besuchern",
            intro: "Nur nach vorheriger Online-Registrierung kostenfrei!",
            items: [
                { text: "Eine kostenfreie Registrierung von <strong>Standpersonal</strong> und <strong>Besucher</strong> ist bis zum 23. März 2026, 23:59:59 Uhr möglich" },
                { text: "Ab dem 24. März 2026, 00:00:00 Uhr ist <strong class='text-danger'>keine</strong> kostenfreie Registrierung mehr möglich, dies betrifft auch die Ticketbuchung über die Mitarbeiterliste" },
                { text: "Bei Einladungen per E-Mail oder anderen Medien bitte über die Bedingungen zur kostenfreien Registrierung informieren!" },
                { text: "Vor Ort haben Besucher und Standpersonal, die sich kostenfrei registriert haben, kostenfreien Zugang zur Veranstaltung" },
                { text: "Besucher ohne vorherige Registrierung können das Ticket kostenpflichtig über eine Online-Registrierung mit Bezahl-Möglichkeit buchen" },
                { text: "Kosten vor Ort:", subItems: ["Tagesticket mit Einladungscode 50 Euro", "Tagesticket ohne Einladungscode 100 Euro"] },
                { text: "Aussteller haben die Möglichkeit insgesamt 5 Personen kostenfrei nachzumelden - dies ist gültig für Neuanmeldungen sowohl für Besucher als auch für Standpersonal" },
                { text: "Die Nachmeldung erfolgt über die Online-Registrierung vor Ort. Bitte Ihren Einladungscode weitergeben und ein <strong>N</strong> voranstellen" },
                { text: "Ab der 6. Nachmeldung hat der Aussteller die Möglichkeit die Kosten zu übernehmen. Kostenabwicklung vor Ort über den Info-Point" },
                { text: "<strong>Ausnahmen gibt es keine!</strong>" }
            ],
            checkbox: "Ich habe diese Punkte gelesen und verstanden und akzeptiere die Bedingungen"
        },
        buttons: {
            saving: "Wird gespeichert...",
            complete: "Onboarding abschließen"
        }
    },
    en: {
        title: "Update Account and Prepare for LOUNGES",
        stepTitle: "2. Onboarding",
        // Section 1
        accountUpdate: {
            title: "Update Account",
            items: [
                { bold: "Check Employee List", text: "Is it still up to date?", subtext: "Add new employees and remove people who are no longer with the company" },
                { bold: "Check Products", text: "Are the listed products still correct?", subtext: "Add new products and remove outdated products" },
                { bold: "Add News", text: "Are the most important company news entered in the system?", subtext: "Add news and check if existing news should remain" }
            ],
            checkbox: "I have read and understood these points and will update the account"
        },
        // Section 2
        imageRights: {
            title: "Usage Rights for Images Uploaded in the System",
            intro: "We need a confirmation here.",
            checkbox: "I hereby confirm that the company has all necessary rights to the uploaded image and that publication does not infringe any third-party rights. I indemnify Inspire GmbH against all third-party claims."
        },
        // Section 3
        boothConstruction: {
            title: "No Custom Booth Construction",
            intro: "Custom booth construction is not permitted at LOUNGES.",
            items: [
                "Special permits must be requested and approved by Inspire GmbH",
                "Only a roll-up or Pixlip wall with a maximum width of 1.00 m and maximum height of 2.50 m may be set up at the booth",
                "If these conditions are not met, the exhibition booth will not be approved",
                "Inspire GmbH is authorized to remove unauthorized booth constructions"
            ],
            checkbox: "I have read and understood these points and accept the conditions"
        },
        // Section 4
        visitorRegistration: {
            title: "Visitor Invitations / Registration of Booth Staff and Visitors",
            intro: "Free only with prior online registration!",
            items: [
                { text: "Free registration for <strong>booth staff</strong> and <strong>visitors</strong> is possible until March 23, 2026, 11:59:59 PM" },
                { text: "From March 24, 2026, 12:00:00 AM, <strong class='text-danger'>no</strong> free registration is possible, this also applies to ticket booking via the employee list" },
                { text: "When sending invitations by email or other media, please inform about the conditions for free registration!" },
                { text: "On-site, visitors and booth staff who have registered for free have free access to the event" },
                { text: "Visitors without prior registration can book a ticket for a fee via online registration with payment option" },
                { text: "On-site costs:", subItems: ["Day ticket with invitation code 50 Euro", "Day ticket without invitation code 100 Euro"] },
                { text: "Exhibitors can register up to 5 people for free as late additions - this applies to new registrations for both visitors and booth staff" },
                { text: "Late registration is done via on-site online registration. Please share your invitation code and prefix it with <strong>N</strong>" },
                { text: "From the 6th late registration, the exhibitor can cover the costs. Cost processing on-site via the Info Point" },
                { text: "<strong>No exceptions!</strong>" }
            ],
            checkbox: "I have read and understood these points and accept the conditions"
        },
        buttons: {
            saving: "Saving...",
            complete: "Complete Onboarding"
        }
    }
};

export default function AdminOnboardingV2Modal({ isOpen, onComplete }) {
    const { i18n } = useTranslation();
    const { user } = useContext(UserContext);

    // Get current language content, fallback to German
    const lang = i18n.language?.startsWith('de') ? 'de' : 'en';
    const c = content[lang];

    const [checkboxes, setCheckboxes] = useState({
        accountUpdate: false,
        imageRights: false,
        boothConstruction: false,
        visitorRegistration: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const allChecked = Object.values(checkboxes).every(Boolean);

    const handleCheckboxChange = (key) => {
        setCheckboxes(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const handleComplete = async () => {
        if (!allChecked || isSubmitting) return;

        setIsSubmitting(true);
        try {
            // Update user conditions with the new onboarding completion
            const existingConditions = user.conditions || [];
            const newCondition = {
                id: ONBOARDING_V2_CONDITION_ID,
                accepted: true,
                acceptedAt: new Date().toISOString()
            };

            // Filter out any existing condition with the same ID and add the new one
            const updatedConditions = [
                ...existingConditions.filter(c => c.id !== ONBOARDING_V2_CONDITION_ID),
                newCondition
            ];

            await client.graphql({
                query: /* GraphQL */ `
                    mutation UpdateUser($input: UpdateUserInput!) {
                        updateUser(input: $input) {
                            id
                            conditions {
                                id
                                accepted
                                acceptedAt
                            }
                        }
                    }
                `,
                variables: {
                    input: {
                        id: user.id,
                        conditions: updatedConditions
                    }
                }
            });

            onComplete();
        } catch (error) {
            console.error("Error completing onboarding V2:", error);
            captureError(error, {
                feature: 'admin-onboarding-v2',
                operation: 'complete-onboarding',
                context: { userId: user?.id },
                tags: { component: 'AdminOnboardingV2Modal' }
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <MDBModal open={isOpen} staticBackdrop tabIndex="-1">
            <MDBModalDialog size="xl" scrollable>
                <MDBModalContent>
                    <MDBModalHeader className="bg-primary text-white">
                        <MDBModalTitle>
                            {c.title}
                        </MDBModalTitle>
                        <div className="ms-auto">
                            <LangSelect style={{ color: 'white' }} />
                        </div>
                    </MDBModalHeader>

                    <MDBModalBody>
                        <MDBTypography tag="h3">
                            <strong>{c.stepTitle}</strong>
                        </MDBTypography>

                        {/* Section 1: Account aktualisieren */}
                        <MDBCard className="mb-4">
                            <MDBCardBody>
                                <MDBCardTitle tag="h5" className="mb-3">
                                    <strong>{c.accountUpdate.title}</strong>
                                </MDBCardTitle>
                                <ul className="mb-3">
                                    {c.accountUpdate.items.map((item, idx) => (
                                        <li key={idx} className={idx > 0 ? "mt-2" : ""}>
                                            <strong>{item.bold}</strong> - {item.text}
                                            <br />
                                            {item.subtext}
                                        </li>
                                    ))}
                                </ul>
                                <MDBCheckbox
                                    id="accountUpdate"
                                    label={c.accountUpdate.checkbox}
                                    checked={checkboxes.accountUpdate}
                                    onChange={() => handleCheckboxChange('accountUpdate')}
                                    className="text-danger fw-bold"
                                />
                            </MDBCardBody>
                        </MDBCard>

                        {/* Section 2: Nutzungsrechte für Bilder */}
                        <MDBCard className="mb-4">
                            <MDBCardBody>
                                <MDBCardTitle tag="h5" className="mb-3">
                                    <strong>{c.imageRights.title}</strong>
                                </MDBCardTitle>
                                <p className="mb-3">
                                    {c.imageRights.intro}
                                </p>
                                <MDBCheckbox
                                    id="imageRights"
                                    label={c.imageRights.checkbox}
                                    checked={checkboxes.imageRights}
                                    onChange={() => handleCheckboxChange('imageRights')}
                                    className="text-danger fw-bold"
                                />
                            </MDBCardBody>
                        </MDBCard>

                        {/* Section 3: Kein eigener Standbau */}
                        <MDBCard className="mb-4">
                            <MDBCardBody>
                                <MDBCardTitle tag="h5" className="mb-3">
                                    <strong>{c.boothConstruction.title}</strong>
                                </MDBCardTitle>
                                <p className="mb-2">
                                    {c.boothConstruction.intro}
                                </p>
                                <ul className="mb-3">
                                    {c.boothConstruction.items.map((item, idx) => (
                                        <li key={idx} className={idx > 0 ? "mt-1" : ""}>{item}</li>
                                    ))}
                                </ul>
                                <MDBCheckbox
                                    id="boothConstruction"
                                    label={c.boothConstruction.checkbox}
                                    checked={checkboxes.boothConstruction}
                                    onChange={() => handleCheckboxChange('boothConstruction')}
                                    className="text-danger fw-bold"
                                />
                            </MDBCardBody>
                        </MDBCard>

                        {/* Section 4: Besuchereinladungen / Registrierung */}
                        <MDBCard className="mb-4">
                            <MDBCardBody>
                                <MDBCardTitle tag="h5" className="mb-3">
                                    <strong>{c.visitorRegistration.title}</strong>
                                </MDBCardTitle>
                                <p className="mb-2 fw-bold">
                                    {c.visitorRegistration.intro}
                                </p>
                                <ul className="mb-3">
                                    {c.visitorRegistration.items.map((item, idx) => (
                                        <li key={idx} className={idx > 0 ? "mt-1" : ""}>
                                            <span dangerouslySetInnerHTML={{ __html: item.text }} />
                                            {item.subItems && (
                                                <ul>
                                                    {item.subItems.map((subItem, subIdx) => (
                                                        <li key={subIdx}>{subItem}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                                <MDBCheckbox
                                    id="visitorRegistration"
                                    label={c.visitorRegistration.checkbox}
                                    checked={checkboxes.visitorRegistration}
                                    onChange={() => handleCheckboxChange('visitorRegistration')}
                                    className="text-danger fw-bold"
                                />
                            </MDBCardBody>
                        </MDBCard>
                    </MDBModalBody>

                    <MDBModalFooter>
                        <MDBBtn
                            color="primary"
                            disabled={!allChecked || isSubmitting}
                            onClick={handleComplete}
                        >
                            {isSubmitting ? (
                                <>
                                    <MDBSpinner size="sm" role="status" className="me-2" />
                                    {c.buttons.saving}
                                </>
                            ) : (
                                c.buttons.complete
                            )}
                        </MDBBtn>
                    </MDBModalFooter>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    );
}
